<template>
  <transition name="modal">
    <div class="modal-container" v-if="show">
      <div class="header" :class="{high: highHeader}">
        <div class="push-div"></div>
        <div class="title-container">
          <h3>{{ title }}</h3>
        </div>
        <button type="button" name="close" @click="close"></button>
      </div>
      <div class="content scrollable">
        <slot name="body">
          <div class="text-content" v-html="markedContent"></div>
        </slot>
      </div>
    </div>
  </transition>
</template>

<script>
  import Vue from 'vue'
  import marked from 'marked'

  export default Vue.component('modal', {
    props: {
      title: {
        type: String,
        default: null
      },
      content: {
        type: String,
        default: ''
      },
      highHeader: {
        type: Boolean,
        default: false
      },
      url: {
        type: String,
        default: ''
      }
    },

    data () {
      return {
        loading: true,
        show: false
      }
    },

    computed: {
      markedContent () {
        return marked(this.content, { sanitize: false, breaks: true })
      }
    },

    mounted () {
      setTimeout(() => {
        this.show = true
      }, 0)
      setTimeout(() => {
        this.bindExternalLinks()
      }, 200)
    },

    methods: {
      close () {
        this.$emit('closeModal')
      },

      loaded () {
        this.loading = false
      },

      bindExternalLinks () {
        const anchors = this.$el.querySelectorAll('.text-content a')

        if (anchors.length > 0) {
          for (let i = 0; i < anchors.length; i++) {
            anchors[i].addEventListener('click', (event) => {
              event.preventDefault()
              window.open(anchors[i].href, '_blank')
            })
          }
        }
      }
    }
  })
</script>

<style lang="scss">
  .modal-container {
    .text-content {
      padding: 30px;

      h1, h2, h3, h4, h5 {
        font-family: Roboto, Arial, sans-serif;
        margin-top: 30px;
        line-height: 1.4;

        &:first-child {
          margin-top: 0;
        }
      }

      h1{
        font-size: 1.75rem;
      }

      h2{
        font-size: 1.5rem;
      }

      h3 {
        font-size: 1.25rem;
      }

      h4 {
        font-size: 1.125rem;
      }

      p {
        font-family: Roboto, Arial, sans-serif;
        margin: 10px 0;
        font-size: .875rem;
        line-height: 16px;
      }

      a {
        color: #303838;
        text-decoration: underline;
        word-wrap: break-word;
      }

      ul {
        padding-left: 20px;
      }
    }

    .external-content {
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;

      width: 100%;
      height: 100%;
      iframe {
        display: block;
        width: 100%;
        height: 100%;
      }
    }

    table {
      font-family: Roboto, Arial, sans-serif;
      font-size: .875rem;

      display: block;
      overflow-x: scroll;

      tr {
        text-align: center;
      }

      th {
        padding: 0 1px;
      }

      td {
        padding: 5px 0;
        vertical-align: middle;
      }

      tr:nth-child(even){
        background: rgba(grey, .1);
      }
    }
  }
</style>

<style lang="scss" scoped>
  @import "../assets/_settings";
  @import "../assets/mixins";

  .modal-container {
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: #fff;
    display: flex;
    z-index: 99999;
    overflow: hidden;
    margin: auto;
    transition: all .5s ease;

    @include desktop {
      position: absolute;
      margin: -30px;
      top: -$section-desktop-margin-top;
      border-radius: 0;
      border-top-right-radius: $moduleBorderRadius;
      border-top-right-radius: $moduleBorderRadius-portal;
      border-bottom-right-radius: $moduleBorderRadius;
      border-bottom-right-radius: $moduleBorderRadius-portal;
    }

    @include desktop-big {
      width: 610px;
      left: -85px;
      top: -160px;
      max-height: 600px;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #303838;
      color: #fff;
      padding: 16px 30px;
      z-index: 100;
      .push-div {
        display: none;
        @include tablet {
          display: inline;
        }
      }
      button {
        background: url('~static/modal_close.svg');
        background-repeat: no-repeat;
        height: 50px;
        margin: -16px -30px;
        width: 50px;
        background-position: center;
        padding: 0;
        box-shadow: none;
        opacity: .75;
        &:hover {
          opacity: 1;
        }
      }

      &.high{
        align-items: flex-start;
        min-height: 100px;
        font-size: 1.375rem;
        padding: 16px 30px 26px 30px;

        @include desktop-big {
          min-height: 20px;
          font-size: 1.0625rem;
          padding: 20px 30px 20px;
        }

        .title-container{
          align-self: flex-end;
          @include tablet {
            width: $tablet-content-width;
          }
        }
      }

      @include desktop-big {
        padding: 21px 30px;
      }

      .title-container {
        @include desktop-big {
          margin: 0 auto;
        }
      }
    }

    .content {
      flex: 1;
      color: #303838;
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;

      @include desktop-big {
        width: 100%;
        margin: 0 auto;
      }

      @include scrollbar;
    }
  }

  .light {
    .header {
      background-color: transparent;
    }
    button[name="close"] {
      background: url('~static/modal_close_light.svg');
      background-size: 35%;
      background-repeat: no-repeat;
      background-position: 50% 50%;
      opacity: .75;
      &:hover {
        opacity: 1;
      }
    }

    @include desktop {
      position: absolute;
      margin: -30px;
    }
  }

  .modal-enter {
    opacity: 0;
    transform: scale(.95);
  }

  .modal-leave-active {
    opacity: 0;
    transform: scale(.95);
  }

  .modal-enter .modal-container,
  .modal-leave-active .modal-container {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
</style>



// WEBPACK FOOTER //
// src/components/Modal.vue